/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import MediaPrepHeaderSection from "../../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../../common/commonComponents/mediaPrepPageHeader";
import { useEffect, useState } from "react";
import ConfirmModal from "./partials/ConfirmModal";
import FinalQualityHeader from "./partials/FinalQualityHeader.";
import { useMediaBatchPrepView } from "../../../../../hooks/useMediaBatchPrepView";
import { MediaBatchPreparationInitServices } from "../../../../../service/mediaBatchPrepInitServices";
import { EsignModal } from "../../../../../widgets/alertModal";
import CustomToast from "../../../../../widgets/toaster";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";


import {
	FinalQualityParameter,
	ParameterEvaluation,
} from "../../types/mediaFinalQuality.types";
import ParamsTable from "./partials/ParamsTable";
import { mediaPreparationService } from "../../../../../service/mediaPrepBatchServices";


interface Props {
	onBack?: () => void;
	batchId: number;
}
const MediaFinalQualityReview = ({ onBack, batchId }: Props) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [showEsignModal, setShowEsignModal] = useState(false);
	const { mediaBatchData, fetchBatchDetails } = useMediaBatchPrepView();
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [parameters, setParameters] = useState<FinalQualityParameter[]>([]);
	const [actionType, setActionType] = useState<"approve" | "reject">("approve");
	const [batchKeyword, setBatchKeyword] = useState("");
	const [evaluations, setEvaluations] = useState<
		Record<string, ParameterEvaluation>
	>({});
	const handleConfirm = () => {
		setShowConfirmModal(false);
		setShowEsignModal(true);
	};

	const [toast, setToast] = useState({
		show: false,
		message: "",
		toastType: "error", // or "error"
	});
	const showToast = (message: string, toastType: "success" | "error") => {
		setToast({ show: true, message, toastType });
	};

	const handleESignConfirm = async (remark: string) => {
		try {
			const payload =
				actionType === "approve"
					? {
							media_batch_id: batchId,
							parameters: Object.values(evaluations).map((item) => ({
								parameter_id: Number(item.parameter_id),
								pass_flag: item.pass_flag,
								...(item.pass_flag === false && {
									fail_count: item.fail_count || 0,
								}),
							})),
							status: "approve",
							remarks: remark,
					}
					: {
							media_batch_id: batchId,
							status: "reject",
							remarks: remark,
					};
			const response =
				await MediaBatchPreparationInitServices.saveFinalQualityReview(
					payload
				);
			if (response?.status) {
				showToast(
					response?.message ||
					"Final quality review saved successfully",
					"success"
				);

				setShowEsignModal(false);

				setTimeout(() => {
					navigate("/mediaPreparationBatch");
				}, 1200);
			}else {
				showToast(
					response?.message ||
					"Failed to save final quality review",
					"error"
				);
			}
		} catch (error: any) {
			showToast(
				error?.response?.data?.message ||
				"An unexpected error occurred",
				"error"
			);
		}
	};

	const getParameters = async () => {
		try {
			const response =
				await MediaBatchPreparationInitServices.getFinalQualityParameters();
			setParameters(response.data);
		} catch (error) {
			console.log(error);
		}
	};

	const validateFailedCounts = () => {
		const newErrors: Record<string, string> = {};
		let valid = true;

		parameters.forEach((parameter) => {
			const evaluation = evaluations[parameter.id];

			if (
				evaluation &&
				evaluation.pass_flag === false &&
				(!evaluation.fail_count || evaluation.fail_count <= 0)
			) {
				newErrors[parameter.id] = t("mediaBatchInit.errors.failedCount");
				valid = false;
			}
		});

		setErrors(newErrors);
		return valid;
	};

	const handleAction = (action: "approve" | "reject") => {
		if (action === "approve" && !validateFailedCounts()) {
			return;
		}
		setActionType(action);
		setShowConfirmModal(true);
	};

	const [batchList, setBatchList] = useState<any[]>([]);
	const getBatchList = async () => {
		try {
			const response = await mediaPreparationService.mediaPrepgetBatchCodes(batchKeyword);

			if (response.status) {
				setBatchList(response.data);
			}
		} catch (err) {
			console.error(err);
		}
	};

	const [, setSearchParams] = useSearchParams();
	const handleBatchChange = (batchId: string) => {
		const selectedBatch = batchList.find(
			(batch) => String(batch.id) === String(batchId)
		);
		if (!selectedBatch) return;

		// Don't navigate for completed batches
		if (
			selectedBatch.status === "Ready to Use" ||
			selectedBatch.status === "Rejected"
		) {
			return;
		}
		setSearchParams({ batchId });
	};

	useEffect(() => {
		if (!batchKeyword) {
			getBatchList();
			return;
		}
		const timer = setTimeout(() => {
			getBatchList();
		}, 300);
		return () => clearTimeout(timer);
	}, [batchId, batchKeyword]);

	useEffect(() => {
		getParameters();
		if (!batchId) return;
		fetchBatchDetails(batchId);
	}, []);

	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("mediaBatchInit.title")} />
			<div className="flex flex-col h-full bg-[#F8FAFC] min-h-screen relative">
				{showConfirmModal && (
					<ConfirmModal
						show={showConfirmModal}
						onClose={() => setShowConfirmModal(false)}
						onConfirm={() => handleConfirm()}
						actionType={actionType}
					/>
				)}
				<EsignModal
					show={showEsignModal}
					onHide={() => setShowEsignModal(false)}
					onConfirm={handleESignConfirm}
					needRemraks={true}
					screenName={"Final Quality Review"}
				/>
				<FinalQualityHeader onBack={onBack} />
				<div className="p-8 w-full max-w-none flex-1 overflow-y-auto space-y-8">
					<div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
						<div className="bg-[#2D3E50] px-6 py-2.5 flex items-center justify-between">
							<h3 className="text-xs font-black text-white uppercase tracking-widest">
								{t("mediaBatchInit.finalQuality.parameters")}
							</h3>
							<div className="flex items-center gap-2">
								<span className="text-[10px] font-black text-white/70 uppercase tracking-widest">
									{t("mediaBatchInit.finalQuality.batch")}:
								</span>

								<div className="relative">
									<Select
										className="w-[180px] text-[12px]"
										value={batchList.map((batch) => ({ value: batch.id, label: batch.media_batch_identifier })).find((o) => o.value === mediaBatchData?.id)}
										options={batchList.map((batch) => ({ value: batch.id, label: batch.media_batch_identifier }))}
										onInputChange={(value, { action }) => {
											if (action === "input-change") setBatchKeyword(value);
										}}
										onChange={(option) => {
											if (option) handleBatchChange(String(option.value));
										}}
										isSearchable
										styles={{
											control: (base, state) => ({ ...base, minHeight: "36px", height: "36px", backgroundColor: "#44546A", border: state.isFocused ? "1px solid #60A5FA" : "1px solid rgba(255,255,255,0.2)", borderRadius: "6px", boxShadow: "none", "&:hover": { border: "1px solid #60A5FA" } }),
											valueContainer: (base) => ({ ...base, height: "36px", padding: "0 12px", display: "flex", alignItems: "center" }),
											singleValue: (base) => ({ ...base, color: "#fff", margin: 0, position: "static" }),
											input: (base) => ({ ...base, color: "#fff", margin: 0, padding: 0 }),
											placeholder: (base) => ({ ...base, color: "rgba(255,255,255,.7)" }),
											menu: (base) => ({ ...base, zIndex: 9999 }),
											indicatorsContainer: () => ({ display: "none" }),
										}}
									/>
									<span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-white/70 text-[18px] pointer-events-none">
									expand_more
									</span>
								</div>
							</div>
						</div>
						<div className="overflow-x-auto">
							<ParamsTable
								evaluations={evaluations}
								setEvaluations={setEvaluations}
								parameters={parameters}
								errors={errors}
    							setErrors={setErrors}
							/>
						</div>
					</div>

					{/* <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
						<div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
							<span className="material-symbols-outlined text-[#FF7043] text-sm">
								draw
							</span>
							<h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-widest">
								Digital Sign-off
							</h3>
						</div>
						<div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-12">
							<div className="space-y-6">
								<div>
									<label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">
										Password *
									</label>
									<input
										type="password"
										className="w-full rounded-lg border-gray-300 py-3 px-4 focus:ring-[#FF7043] focus:border-[#FF7043]"
										placeholder="••••••••"
									/>
								</div>
							</div>

							<div className="bg-blue-50 border border-blue-100 rounded-xl p-6 relative">
								<div className="absolute top-0 left-6 -translate-y-1/2 bg-blue-600 text-white px-3 py-0.5 rounded text-[8px] font-black uppercase tracking-widest">
									Legal Certification
								</div>
								<p className="text-xs font-medium text-blue-900 leading-relaxed mb-6">
									"I, the undersigned supervisor, hereby certify that
									the above batch has been validated against
									established standard operating procedures
									(SOP-QC-004) and meets all pre-defined quality
									release criteria for distribution and use."
								</p>
								<label className="flex items-start gap-3 cursor-pointer group">
									<input
										type="checkbox"
										className="mt-0.5 rounded text-blue-600 focus:ring-blue-600"
									/>
									<span className="text-[11px] font-bold text-blue-900 leading-tight">
										I authorize this batch for final release and
										confirm audit trail integrity.
									</span>
								</label>
							</div>
						</div>
					</div> */}

					<div className="flex justify-end gap-4 pt-2">
					<button
						onClick={() => handleAction("approve")}
						className="bg-[#FF7043] text-white flex items-center text-[11px] gap-2 px-6 py-2.5 rounded-2xl shadow-xl shadow-orange-500/30 font-black uppercase tracking-widest hover:brightness-110 active:scale-95 transition-all border-none disabled:opacity-50"
					>
						<span className="material-symbols-outlined text-lg">
							{t("mediaBatchInit.finalQuality.verified")} 
						</span>
						{t("mediaBatchInit.finalQuality.approve")}
					</button>

					<button
						onClick={() => handleAction("reject")}
						className="flex items-center gap-2 px-8 py-2.5 text-[11px] font-black text-slate-500 uppercase tracking-widest hover:text-red-500 transition-all border-none rounded-2xl"
					>
						<span className="material-symbols-outlined text-lg">
							{t("mediaBatchInit.finalQuality.cancel")}
						</span>
						{t("mediaBatchInit.finalQuality.reject")}
					</button>
				</div>
				</div>
			</div>
			<CustomToast
			show={toast.show}
			message={toast.message}
			toastType={toast.toastType}
			delay={1200}
			onClose={() =>
				setToast((prev) => ({
					...prev,
					show: false,
				}))
			}
			toastPosition="topRight"
		/>
		</div>
	);
};

export default MediaFinalQualityReview;

